import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { fetchUser } from '../services/api';
import { UserData } from '../types/types';

interface UserContextType {
  user: UserData | null;
  setUser: React.Dispatch<React.SetStateAction<UserData | null>>;
  isAuthenticated: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  refreshUser: (token: string | null) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() =>
    Boolean(sessionStorage.getItem('token'))
  );

  const token = sessionStorage.getItem('token');

  const refreshUser = async (token: string | null) => {
    if (!token) {
      setUser(null);
      setIsAuthenticated(false);
      return;
    }
    try {
      const userData = await fetchUser(token);
      setUser(userData);
      setIsAuthenticated(true);
    } catch (err) {
      console.error('Failed to fetch user in context:', err);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    refreshUser(token);
  }, [token]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        isAuthenticated,
        setIsAuthenticated,
        refreshUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within a UserProvider');
  return ctx;
};
